import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { LocaleSwitcher } from "./LocaleSwitcher";
import { ThemeSwitcher } from "./ThemeSwitcher";
import { UserMenu } from "./UserMenu";
import { Button } from "./ui/button";
import { useCaller } from "../auth/useCaller";
import { cn } from "@/lib/cn";

type NavItem = {
  to: string;
  labelKey: string;
  adminOnly?: boolean;
  // When true, hidden from OPERATORs (who only see /tournaments).
  hideForOperator?: boolean;
  // When true, hidden from ASSISTANTs (they can't manage other assistants).
  hideForAssistant?: boolean;
};

const NAV_ITEMS: NavItem[] = [
  { to: "/tournaments", labelKey: "nav.tournaments" },
  { to: "/athletes", labelKey: "nav.athletes", hideForOperator: true },
  { to: "/dojos", labelKey: "nav.dojos", hideForOperator: true },
  { to: "/payments", labelKey: "nav.payments", hideForOperator: true },
  { to: "/attendance", labelKey: "nav.attendance", hideForOperator: true },
  { to: "/coaches", labelKey: "nav.coaches", adminOnly: true },
  { to: "/operators", labelKey: "nav.operators", adminOnly: true },
  { to: "/assistants", labelKey: "nav.assistants", hideForOperator: true, hideForAssistant: true },
  // Reports is admin-OR-coach: coaches see a filtered subset.
  { to: "/reports", labelKey: "nav.reports", hideForOperator: true },
  { to: "/inventory", labelKey: "nav.inventory", adminOnly: true }
];

export function AppLayout() {
  const { t } = useTranslation();
  const { caller } = useCaller();
  const isAdmin = caller?.groups.includes("ADMIN") ?? false;
  const isOperator =
    !isAdmin && (caller?.groups.includes("OPERATOR") ?? false);
  const isAssistant =
    !isAdmin && (caller?.groups.includes("ASSISTANT") ?? false);
  const visible = NAV_ITEMS.filter((item) => {
    if (item.adminOnly && !isAdmin) return false;
    if (isOperator && item.hideForOperator) return false;
    if (isAssistant && item.hideForAssistant) return false;
    return true;
  });

  // Mobile drawer state. Open below `md`, hidden above. Closed automatically
  // whenever the route changes so a tap on a link dismisses the menu.
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 md:gap-8 min-w-0">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="md:hidden h-10 w-10"
            aria-label={t("nav.toggleMenu")}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <NavLink to="/" className="text-xl font-bold text-primary truncate">
            DojoMatch
          </NavLink>
          <nav
            className="hidden md:flex items-center gap-1"
            aria-label={t("nav.primary")}
          >
            {visible.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "px-3 py-2 rounded-md text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-accent"
                  )
                }
              >
                {t(item.labelKey)}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <ThemeSwitcher />
          <UserMenu />
        </div>
      </header>

      {/* Mobile nav drawer. Renders below the header at <md and slides into
          view when the hamburger is open. */}
      {open && (
        <nav
          className="md:hidden border-b border-border bg-background"
          aria-label={t("nav.primary")}
        >
          <ul className="flex flex-col py-2">
            {visible.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      "block px-4 py-3 text-base font-medium",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-foreground hover:bg-accent"
                    )
                  }
                >
                  {t(item.labelKey)}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <main className="flex-1 px-4 sm:px-6 py-6 sm:py-8">
        <Outlet />
      </main>

      <footer className="border-t border-border px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-0 sm:justify-between text-sm text-muted-foreground">
        <span>&copy; {new Date().getFullYear()} DojoMatch</span>
        <LocaleSwitcher />
      </footer>
    </div>
  );
}
